import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { Card, CardContent, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { INK, NEUTRAL, STATUS } from "../../../theme/tokens";
import { AgentStaffAction, AgentStaffActionStatus, CallStatus, type FlatNurseAction } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

interface AcceptedNursesCardsProps {
	allActions: FlatNurseAction[];
}

const formatDateTime = (isoTime: string) => DateTime.fromISO(isoTime).toFormat("MMM d, h:mm a");

const earliestActionTime = (actions: FlatNurseAction[]): string | null => {
	const times = actions.map((a) => a.action_time).filter((t): t is string => !!t);
	if (times.length === 0) return null;
	return times.reduce((earliest, t) => (t < earliest ? t : earliest));
};

const latestActionTime = (actions: FlatNurseAction[]): string | null => {
	const times = actions.map((a) => a.action_time).filter((t): t is string => !!t);
	if (times.length === 0) return null;
	return times.reduce((latest, t) => (t > latest ? t : latest));
};

type NurseCardData = {
	acceptedAt: string | null;
	smsSentAt: string | null;
	hasCall: boolean;
	callAnswered: boolean;
	callTime: string | null;
	hasIcons: boolean;
};

const getNurseCardData = (nurseActions: FlatNurseAction[]): NurseCardData => {
	const acceptedAt = nurseActions.find((a) => a.accepted_at)?.accepted_at ?? null;

	const completedActions = nurseActions.filter((a) => a.status === AgentStaffActionStatus.Completed);
	const sentSmsActions = completedActions.filter((a) => a.type === AgentStaffAction.Message);
	const placedCallActions = completedActions.filter((a) => a.type === AgentStaffAction.Call);

	const smsSentAt = earliestActionTime(sentSmsActions);

	const answeredCall = placedCallActions.find((a) => a.response_status === CallStatus.Completed);
	const callAnswered = Boolean(answeredCall);
	const callTime = callAnswered ? (answeredCall?.action_time ?? null) : latestActionTime(placedCallActions);
	const hasCall = placedCallActions.length > 0;

	const hasIcons = Boolean(smsSentAt) || hasCall;

	return { acceptedAt, smsSentAt, hasCall, callAnswered, callTime, hasIcons };
};

export const AcceptedNursesCards: FC<AcceptedNursesCardsProps> = ({ allActions }) => {
	const { getNurseFormattedName } = useHospitalData();

	const approvedActionsByNurse = useMemo(() => {
		const map = new Map<number, FlatNurseAction[]>();
		for (const action of allActions) {
			if (!action.is_approved) continue;
			const existing = map.get(action.nurse_id) ?? [];
			existing.push(action);
			map.set(action.nurse_id, existing);
		}
		return map;
	}, [allActions]);

	if (approvedActionsByNurse.size === 0) {
		return null;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
			{Array.from(approvedActionsByNurse.entries()).map(([nurseId, nurseActions]) => {
				const nurseName = getNurseFormattedName(nurseId);
				const { acceptedAt, smsSentAt, hasCall, callAnswered, callTime, hasIcons } = getNurseCardData(nurseActions);

				return (
					<Card key={nurseId} sx={{ border: `1px solid ${NEUTRAL[150]}`, borderRadius: "4px" }}>
						<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CheckCircleOutlineIcon sx={{ fontSize: "16px", color: STATUS.SUCCESS.main }} />
										<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px" }}>
											{nurseName || `Nurse ${nurseId}`} accepted the shift
										</Typography>
									</Box>
									{hasIcons && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{smsSentAt && (
												<InHouseTooltip title={`SMS sent at ${formatDateTime(smsSentAt)}`}>
													<SmsOutlinedIcon sx={{ fontSize: "16px", color: INK.SECONDARY }} />
												</InHouseTooltip>
											)}
											{hasCall && (
												<InHouseTooltip title={`Call ${callAnswered ? "answered" : "not answered"}${callTime ? ` at ${formatDateTime(callTime)}` : ""}`}>
													{callAnswered ? (
														<PhoneIcon sx={{ fontSize: "16px", color: STATUS.SUCCESS.main }} />
													) : (
														<PhoneMissedIcon sx={{ fontSize: "16px", color: STATUS.ERROR.main }} />
													)}
												</InHouseTooltip>
											)}
										</Box>
									)}
								</Box>
								{acceptedAt && (
									<Typography variant="body2" sx={{ color: INK.MUTED, fontSize: "12px", fontWeight: 400 }}>
										Confirmed at {formatDateTime(acceptedAt)}
									</Typography>
								)}
							</Box>
						</CardContent>
					</Card>
				);
			})}
		</Box>
	);
};
